import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const NFTMinting = () => {
  const [mintQuantity, setMintQuantity] = useState(1);
  const [ethUsdPrice, setEthUsdPrice] = useState(3184.58); // Current ETH/USD price from Chainlink

  const nftPrice = 0.24; // ETH price
  const nftPriceUsd = nftPrice * ethUsdPrice;
  const totalPrice = mintQuantity * nftPrice;
  const totalPriceUsd = totalPrice * ethUsdPrice;
  const remainingNFTs = 8234; // Example remaining count
  const mintingUrl = "https://og.rarible.com/token/0xb66a603f4cfe17e3d27b87a8bfcad319856518b8:66443457087237224376496623462586478899471087482130632045442236354399968428034";

  // Fetch ETH price from Kraken public API (reliable, no rate limits)
  const fetchEthPrice = async () => {
    try {
      const response = await fetch('https://api.kraken.com/0/public/Ticker?pair=ETHUSD');
      const data = await response.json();
      if (data.result && data.result.XETHZUSD && data.result.XETHZUSD.c) {
        // 'c' is the last trade closed array, first element is price
        const price = parseFloat(data.result.XETHZUSD.c[0]);
        setEthUsdPrice(price);
      }
    } catch (error) {
      console.error('Error fetching ETH price:', error);
      // Keep existing price if fetch fails
    }
  };

  // Fetch price on component mount and every hour
  useEffect(() => {
    fetchEthPrice(); // Initial fetch
    const interval = setInterval(() => {
      fetchEthPrice();
    }, 3600000); // Update every hour (3600000 ms)

    return () => clearInterval(interval);
  }, []);

  return (
    <section id="minting" className="py-20 bg-gradient-to-b from-gray-900 to-gray-800 relative overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img 
          src="https://images.unsplash.com/photo-1606364136354-0f6d846767b7"
          alt="NFT Cards"
          className="w-full h-full object-cover opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 to-purple-900/30"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Mint Your </span>
            <span className="text-yellow-400">VIP Pass</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Join the exclusive Octagon Infinity community and unlock premium investment opportunities. 
            Each NFT is your gateway to high-yield European real estate projects.
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-12">
          {/* NFT Preview */}
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-r from-purple-600/20 to-cyan-600/20 backdrop-blur-lg rounded-3xl p-8 border border-gray-700">
              <video 
                src="https://customer-assets.emergentagent.com/job_vip-passes/artifacts/m1ws5dfz_Card%20-%20VIP%20prereveal%20vid.mp4"
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-80 object-contain rounded-2xl mb-6 bg-gray-900"
              />
              
              <h3 className="text-2xl font-bold text-white mb-4">Octagon Infinity NFT</h3>
              <p className="text-gray-300 mb-6">
                We are not offering you just NFTs, they are VIP passes to our investment platform to generate wealth and be part of an investment community. These cards are also used for collecting your earnings from our investments.
              </p>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-800/50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-purple-400">10,000</div>
                  <div className="text-gray-400 text-sm">Total Supply</div>
                </div>
                <div className="bg-gray-800/50 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-cyan-400">{remainingNFTs.toLocaleString()}</div>
                  <div className="text-gray-400 text-sm">Remaining</div>
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div className="space-y-4">
              <h4 className="text-xl font-semibold text-white">NFT Benefits:</h4>
              {[
                "VIP access to exclusive investment projects",
                "€4,000 investment power per NFT",
                "Participate in project profits",
                "Priority access to future NFT drops"
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  className="flex items-center space-x-3"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-gray-300">{benefit}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Minting Interface */}
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div className="bg-gray-800/50 backdrop-blur-lg rounded-3xl p-8 border border-gray-700">
              <h3 className="text-3xl font-bold text-white mb-6 text-center">
                Mint Your NFTs
              </h3>

              {/* Quantity Selector */}
              <div className="mb-6">
                <label className="block text-gray-300 mb-3">Quantity (Max 10 per wallet)</label>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setMintQuantity(Math.max(1, mintQuantity - 1))}
                    className="w-12 h-12 bg-gray-700 hover:bg-gray-600 rounded-xl flex items-center justify-center text-xl font-bold transition-colors duration-200"
                    disabled={mintQuantity <= 1}
                  >
                    -
                  </button>
                  <div className="flex-1 text-center">
                    <div className="text-4xl font-bold text-white">{mintQuantity}</div>
                  </div>
                  <button
                    onClick={() => setMintQuantity(Math.min(4, mintQuantity + 1))}
                    className="w-12 h-12 bg-gray-700 hover:bg-gray-600 rounded-xl flex items-center justify-center text-xl font-bold transition-colors duration-200"
                    disabled={mintQuantity >= 4}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Price Display */}
              <div className="bg-gray-700/50 rounded-2xl p-6 mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-300">Price per NFT:</span>
                  <div className="text-right">
                    <div className="text-white font-semibold">{nftPrice} ETH</div>
                    <div className="text-sm text-gray-400">≈ ${nftPriceUsd.toFixed(2)} USD</div>
                  </div>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-300">Quantity:</span>
                  <span className="text-white font-semibold">{mintQuantity}</span>
                </div>
                <div className="border-t border-gray-600 pt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-white">Total:</span>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-purple-400">{totalPrice.toFixed(2)} ETH</div>
                      <div className="text-sm text-gray-400">≈ ${totalPriceUsd.toFixed(2)} USD</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Minting Button */}
              <motion.a
                href={mintingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white py-4 rounded-2xl text-xl font-semibold transition-all duration-300 text-center"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-center justify-center space-x-2">
                  <SparklesIcon className="w-6 h-6" />
                  <span>Mint Now</span>
                </div>
              </motion.a>

              <p className="text-sm text-gray-400 text-center mt-4">
                By minting, you agree to our terms and conditions. Max 10 NFTs per wallet. Gas fees may apply.
              </p>
            </div>

            {/* Rarity Information */}
            <div className="bg-gradient-to-r from-yellow-600/20 to-amber-600/20 backdrop-blur-lg rounded-2xl p-6 border border-yellow-500/30">
              <h4 className="text-lg font-semibold text-yellow-400 mb-3">Rarity Tiers</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-green-400">Green (60%)</span>
                  <span className="text-gray-400">Standard benefits</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-orange-400">Bronze (30%)</span>
                  <span className="text-gray-400">1.2x investment power</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-300">Silver (8%)</span>
                  <span className="text-gray-400">1.5x investment power</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-yellow-400">Gold (2%)</span>
                  <span className="text-gray-400">2x investment power</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-600">
                  <span className="text-yellow-300 text-xs">Legendary 2% will be in the Octagon automatically</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default NFTMinting;